import React, { Suspense, useEffect, useState, useRef } from "react";

import homePagePayload from "../../config/homePayload";
import homePageAPI from "../../api/homePageApi";

import CouponFlash from "./CouponFlash";
import { Link } from "react-router-dom";
import { Row, Col, Container } from "react-bootstrap";

import {
  Swiper,
  SwiperSlide,
  Autoplay,
  Navigation,
  Pagination,
} from "./Shared/CustomSwiper";
import { FaBox, FaCheckCircle, FaHeadset } from "react-icons/fa";
import VideoSection from "./VideoSection";
import { useSiteConfig } from "../../context/SiteConfigContext";

const Product = React.lazy(() => import("../products/Card"));
const Testimonials = React.lazy(() => import("../testimonials/Card"));
const CategoryCard = React.lazy(() => import("../categories/Card"));
const Blog = React.lazy(() => import("../blogs/Card"));
const Banner = React.lazy(() => import("./Banner"));

function Home() {
  const { config } = useSiteConfig();

  const [coupons, setCoupons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [products, setProducts] = useState([]);
  const [topSellerProducts, setTopSellerProducts] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [topBanners, setTopBanners] = useState([]);
  const [middleBanners, setMiddleBanners] = useState([]);
  const firstMiddleBanner = middleBanners[0];
  const secondMiddleBanner = middleBanners[1];

  const parallaxRef = useRef(null);
  const [parallaxOffset, setParallaxOffset] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!parallaxRef.current) return;
      const rect = parallaxRef.current.getBoundingClientRect();
      const viewportCenter = window.innerHeight / 2;
      const elementCenter = rect.top + rect.height / 2;
      const distance = viewportCenter - elementCenter;
      setParallaxOffset(distance * 0.15); // 0.15 = parallax speed
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    fetchHomePageDetails();
  }, []);

  const CACHE_TIME = 3 * 60 * 1000;

  const applyData = (data) => {
    setTopBanners(data?.banners?.filter((b) => b.position === "top"));
    setMiddleBanners(data?.banners?.filter((b) => b.position === "middle"));
    setCoupons(data?.coupons);
    setCategories(data?.categories);
    setTestimonials(data?.testimonials);
    setBlogs(data?.blogs);
    setProducts(data?.products);
    setTopSellerProducts(data?.topSellerProducts);
  };

  const fetchHomePageDetails = async () => {
    try {
      const cached = sessionStorage.getItem("homeData");
      const parsed = cached ? JSON.parse(cached) : null;

      if (parsed) applyData(parsed.data);

      if (!parsed || Date.now() - parsed.timestamp > CACHE_TIME) {
        const res = await homePageAPI.homePage(homePagePayload);
        if (res.code === 200) {
          sessionStorage.setItem(
            "homeData",
            JSON.stringify({ data: res.data, timestamp: Date.now() }),
          );
          if (!parsed) applyData(res.data); // only update state if no cache was shown
        }
      }
    } catch (error) {
      console.error("Home API Error:", error);
    }
  };

  return (
    <>
      {/* show banners */}
      {topBanners && topBanners.length > 0 && (
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          slidesPerView={1}
          pagination={{ clickable: true }}
          autoplay={{ delay: 3000 }}
          loop={true}
        >
          {topBanners.map((banner, index) => (
            <SwiperSlide key={index}>
              <Banner item={banner} />
            </SwiperSlide>
          ))}
        </Swiper>
      )}

      {/* Coupon Bar */}
      {coupons && coupons.length > 0 && (
        <section className="custom-coupon-section">
          <div className="custom-coupon-marquee-wrap">
            <div className="custom-coupon-marquee">
              {coupons.map((coupon, index) => (
                <CouponFlash key={coupon._id || index} item={coupon} />
              ))}

              {/* DUPLICATE FOR SMOOTH LOOP */}
              {coupons.map((coupon, index) => (
                <CouponFlash
                  key={`duplicate-${coupon._id || index}`}
                  item={coupon}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* category listing */}
      {categories && categories.length > 0 && (
        <section className="custom-categories-section">
          <div className="container">
            {/* SECTION HEADING */}
            <div className="custom-categories-heading text-center">
              <span className="custom-categories-eyebrow">
                EXPLORE OUR COLLECTION
              </span>

              <h2>
                CHOOSE YOUR <span>PERFECT RIDE</span>
              </h2>
            </div>

            {/* CATEGORY CARDS */}
            <div className="row g-4">
              <Suspense fallback={<div></div>}>
                {categories.slice(0, 3).map((category, index) => (
                  <div
                    className="col-lg-4 col-md-6 col-12"
                    key={category?.id || index}
                  >
                    <CategoryCard item={category} index={index} />
                  </div>
                ))}
              </Suspense>
            </div>

            {/* VIEW ALL */}
            <div className="custom-categories-view-all">
              <Link to="/categories" className="custom-view-all-btn">
                <span className="custom-view-all-icon">⠿</span>
                View All Categories
                <span className="custom-view-all-arrow">→</span>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* middle banners */}
      {firstMiddleBanner && (
        <section className="custom-banner-section">
          <div className="custom-banner-box">
            {/* Decorative Elements */}
            <div className="custom-banner-speed-lines"></div>
            <div className="custom-banner-dots"></div>

            {/* LEFT CONTENT */}
            <div className="custom-banner-content">
              <span className="custom-banner-tag">BEST PERFORMANCE SERIES</span>

              <h2>{firstMiddleBanner.title}</h2>

              <p>{firstMiddleBanner.subTitle}</p>

              {/* FEATURES */}
              <div className="custom-banner-features">
                <div className="custom-banner-feature">
                  <span>01</span>
                  <p>LIGHTWEIGHT</p>
                </div>

                <div className="custom-banner-feature">
                  <span>02</span>
                  <p>PRECISION</p>
                </div>

                <div className="custom-banner-feature">
                  <span>03</span>
                  <p>CONTROL</p>
                </div>
              </div>

              <Link
                to="/shop"
                className="custom-banner-btn text-decoration-none"
              >
                <span>Explore Collection</span>
                <b>→</b>
              </Link>
            </div>

            {/* RIGHT CYCLE */}
            <div className="custom-banner-image">
              <div className="custom-banner-track"></div>

              <div className="custom-banner-image-glow"></div>

              <img
                src={firstMiddleBanner.image}
                alt={firstMiddleBanner.title || "Featured Cycle"}
              />
            </div>
          </div>
        </section>
      )}

      {/* bestsellers */}
      {products && products.length > 0 && (
        <section className="custom-products-section">
          <div className="container">
            {/* SECTION HEADING */}
            <div className="custom-products-heading">
              <div>
                <span className="custom-products-eyebrow">
                  BESTSELLER PRODUCTS
                </span>

                <h2>
                  RIDE <span>YOUR WAY.</span>
                </h2>
              </div>

              <Link to="/shop" className="custom-products-view-all">
                View All Bikes
                <span>→</span>
              </Link>
            </div>

            {/* PRODUCTS */}
            <div className="row g-2">
              <Suspense fallback={<div></div>}>
                {products.map((product, index) => (
                  <div
                    className="col-xl-3 col-lg-4 col-md-6 col-12"
                    key={product?._id || index}
                  >
                    <Product item={product} />
                  </div>
                ))}
              </Suspense>
            </div>
          </div>
        </section>
      )}

      {/* second middle section */}
      {secondMiddleBanner && (
        <section
          className="custom-middle-banner"
          style={{
            backgroundImage: `url(${secondMiddleBanner.image})`,
          }}
        >
          <div className="custom-middle-overlay"></div>

          <div className="container">
            <div className="custom-middle-content">
              <span>BEST RIDE SERIES OF FAST RIDER</span>

              <h2>{secondMiddleBanner.title}</h2>

              <p>{secondMiddleBanner.subTitle}</p>

              <Link to="/shop" className="custom-middle-btn">
                Discover More
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* trending */}
      {topSellerProducts && topSellerProducts.length > 0 && (
        <section className="custom-products-section">
          <div className="container ">
            <div className="custom-products-heading">
              <div>
                <span className="custom-products-eyebrow">
                  TRENDING PRODUCTS
                </span>

                <h2>
                  RIDE <span>YOUR WAY.</span>
                </h2>
              </div>

              <Link to="/shop" className="custom-products-view-all">
                View All Bikes
                <span>→</span>
              </Link>
            </div>
            <Row className="g-3">
              <Suspense fallback={<div></div>}>
                {topSellerProducts.map((product, index) => (
                  <Col
                    lg={3}
                    md={6}
                    sm={6}
                    xs={12}
                    key={[product._id || index]}
                  >
                    <Product item={product} />
                  </Col>
                ))}
              </Suspense>
            </Row>
          </div>
        </section>
      )}

      <VideoSection />

      {testimonials && testimonials.length > 0 && (
        <section className="custom-testimonial-section">
          <div className="container">
            <div className="custom-products-heading">
              <div>
                <span className="custom-products-eyebrow">
                  CUSTOMER REVIEWS
                </span>

                <h2>
                  WHAT RIDER <span>SAYS.</span>
                </h2>
              </div>
            </div>
            <Swiper
              modules={[Navigation, Autoplay]}
              spaceBetween={20}
              slidesPerView={1}
              pagination={{ clickable: true }}
              autoplay={{ delay: 2500 }}
              breakpoints={{
                0: { slidesPerView: 1.2 },
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
              }}
              className="testimonial-swiper"
            >
              <Suspense fallback={<div></div>}>
                {testimonials.map((testimonial, index) => (
                  <SwiperSlide key={index}>
                    <Testimonials item={testimonial} />
                  </SwiperSlide>
                ))}
              </Suspense>
            </Swiper>
          </div>
        </section>
      )}

      {blogs && blogs.length > 0 && (
        <section className="blog-section">
          <Container>
            <div className="custom-products-heading">
              <div>
                <span className="custom-products-eyebrow">TESTIMONIALS</span>

                <h2>
                  OUR LATEST <span>STORIES.</span>
                </h2>
              </div>

              <Link to="/blog" className="custom-products-view-all">
                View All Blogs
                <span>→</span>
              </Link>
            </div>

            <Swiper
              modules={[Pagination, Autoplay]}
              spaceBetween={20}
              slidesPerView={1}
              // autoplay={{ delay: 3500 }}
              pagination={{ clickable: true }}
              breakpoints={{
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
              }}
              className="blog-swiper"
            >
              <Suspense fallback={<div></div>}>
                {blogs.map((blog, index) => (
                  <SwiperSlide key={blog.id}>
                    <Blog item={blog} />
                  </SwiperSlide>
                ))}
              </Suspense>
            </Swiper>
          </Container>
        </section>
      )}

      <section className="map-section">
        {config?.address?.mapLocation && (
          <div className="homepage-wg-map">
            <iframe
              src={config?.address?.mapLocation}
              className="map"
              style={{ border: "none" }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        )}
      </section>
    </>
  );
}

export default Home;
